import { useQuery } from '@tanstack/react-query';
import { Box, Button, Grid, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ElectricScooterOutlinedIcon from '@mui/icons-material/ElectricScooterOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { endpoints, qk } from '../api/endpoints';
import { KpiTile } from '../components/KpiTile';
import { CloneTable, type HealthFilter } from '../components/CloneTable';
import { AlertList } from '../components/AlertList';
import { ErrorState, PageHeader, RelTime, SectionCard, SkeletonRows } from '../components/common';
import { fmtMoney, fmtNumber, fmtPct } from '../lib/format';
import { useIsOwner } from '../auth/AuthProvider';

export const REFRESH_MS = 30_000;

function ServerMeter({ label, icon, pct, loading }: { label: string; icon: React.ReactNode; pct: number | null; loading: boolean }) {
  const color = (pct ?? 0) >= 93 ? 'error' : (pct ?? 0) >= 85 ? 'warning' : 'primary';
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', mb: 0.5 }}>
        <Box sx={{ display: 'flex', fontSize: 20 }}>{icon}</Box>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} color={color === 'primary' ? 'text.primary' : `${color}.main`}>
          {loading ? '…' : fmtPct(pct)}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={Math.min(100, pct ?? 0)} color={color} sx={{ height: 6, borderRadius: 3 }} aria-label={`${label} ${pct ?? 0}%`} />
    </Box>
  );
}

export function OverviewPage() {
  const owner = useIsOwner();
  const [params, setParams] = useSearchParams();
  const filter = (params.get('health') as HealthFilter) || 'all';
  const q = useQuery({ queryKey: qk.overview, queryFn: endpoints.overview, refetchInterval: REFRESH_MS });
  const d = q.data;
  const t = d?.totals;
  const loading = q.isLoading;
  const revenue = Object.entries(t?.revenueMonthByCurrency ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={
          q.dataUpdatedAt ? (
            <span>
              Updated <RelTime iso={new Date(q.dataUpdatedAt).toISOString()} /> · refreshes every 30 s
            </span>
          ) : (
            'All installations at a glance'
          )
        }
        actions={
          <>
            <Tooltip title="Refresh now">
              <IconButton onClick={() => void q.refetch()} aria-label="Refresh">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {owner && (
              <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="/clones/new">
                New clone
              </Button>
            )}
          </>
        }
      />
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} lg>
          <KpiTile label="Clones running" icon={<LayersOutlinedIcon fontSize="inherit" />} loading={loading} value={t ? `${t.running} / ${t.clones}` : '—'} sub={t && t.clones - t.running > 0 ? `${t.clones - t.running} not running` : 'all running'} />
        </Grid>
        <Grid item xs={6} sm={4} lg>
          <KpiTile label="Down" icon={<ReportProblemOutlinedIcon fontSize="inherit" />} loading={loading} value={fmtNumber(t?.down)} tone={t && t.down > 0 ? 'error' : 'success'} sub={t && t.down > 0 ? 'needs action now' : 'none down'} />
        </Grid>
        <Grid item xs={6} sm={4} lg>
          <KpiTile label="Rides today" icon={<RouteOutlinedIcon fontSize="inherit" />} loading={loading} value={fmtNumber(t?.ridesToday)} sub={`${fmtNumber(t?.ridesMonth)} in 30 days`} />
        </Grid>
        <Grid item xs={6} sm={4} lg>
          <KpiTile
            label="Revenue 30 days"
            icon={<PaymentsOutlinedIcon fontSize="inherit" />}
            loading={loading}
            value={revenue.length ? fmtMoney(revenue[0][1], revenue[0][0], { compact: true }) : '—'}
            sub={revenue.length > 1 ? revenue.slice(1).map(([c, v]) => fmtMoney(v, c, { compact: true })).join(' · ') : revenue.length ? 'one currency' : 'no data yet'}
          />
        </Grid>
        <Grid item xs={6} sm={4} lg>
          <KpiTile label="Active rides now" icon={<PlayCircleOutlineIcon fontSize="inherit" />} loading={loading} value={fmtNumber(t?.activeRidesNow)} />
        </Grid>
        <Grid item xs={6} sm={4} lg>
          <KpiTile label="Scooters" icon={<ElectricScooterOutlinedIcon fontSize="inherit" />} loading={loading} value={fmtNumber(t?.scooters)} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5} lg={4}>
          <SectionCard
            title="Server x123"
            action={
              d?.server.checkedAt && (
                <Typography variant="caption" color="text.secondary">
                  <RelTime iso={d.server.checkedAt} prefix="checked " />
                </Typography>
              )
            }
          >
            <Stack spacing={2}>
              <ServerMeter label="Disk /srv" icon={<StorageOutlinedIcon fontSize="inherit" />} pct={d?.server.diskUsedPct ?? null} loading={loading} />
              <ServerMeter label="Memory" icon={<MemoryOutlinedIcon fontSize="inherit" />} pct={d?.server.memUsedPct ?? null} loading={loading} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                <SpeedOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Load, 1 min
                </Typography>
                <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtNumber(d?.server.load1, 2)}
                </Typography>
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={7} lg={8}>
          <SectionCard
            title={`Open alerts${d ? ` (${d.alerts.length})` : ''}`}
            action={
              <Button size="small" component={RouterLink} to="/alerts">
                All alerts
              </Button>
            }
          >
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>{loading ? <SkeletonRows rows={3} height={48} /> : <AlertList alerts={d?.alerts ?? []} compact />}</Box>
          </SectionCard>
        </Grid>
      </Grid>

      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        Clones
      </Typography>
      {loading ? (
        <SkeletonRows rows={4} height={52} />
      ) : (
        <CloneTable
          clones={d?.clones ?? []}
          filter={filter}
          onFilterChange={(f) => {
            const p = new URLSearchParams(params);
            if (f === 'all') p.delete('health');
            else p.set('health', f);
            setParams(p, { replace: true });
          }}
        />
      )}
    </>
  );
}

export function ClonesPage() {
  const owner = useIsOwner();
  const q = useQuery({ queryKey: qk.clones, queryFn: endpoints.clones, refetchInterval: REFRESH_MS });
  return (
    <>
      <PageHeader
        title="Clones"
        subtitle="Every client installation on x123"
        actions={
          owner && (
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="/clones/new">
              New clone
            </Button>
          )
        }
      />
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.isLoading ? <SkeletonRows rows={4} height={52} /> : <CloneTable clones={q.data ?? []} />}
    </>
  );
}
